import type { Root as MdastRoot } from 'mdast';
import type { Processor } from 'unified';

/**
 * Shared lazy loader for the unified / remark / rehype ESM bundle.
 *
 * unified and its plugins ship pure ESM, which breaks Jest configs that
 * don't transform them. Loading lazily on first call (instead of at
 * module-eval time) keeps server + common test suites that don't
 * actually invoke markdown conversion working without a broad
 * `transformIgnorePatterns` rewrite.
 *
 * Both `markdown-to-html` and `markdown-to-aline` need a unified
 * processor with `remark-parse` + `remark-gfm`; this helper produces
 * one (plus a factory for fresh chained processors).
 *
 * The `import type` lines above carry zero runtime cost — they're
 * erased by tsc — so the lazy-load guarantee is preserved.
 */
export async function loadRemarkProcessor(): Promise<{
  baseProcessor: Processor<MdastRoot>;
  createProcessor: () => Processor<MdastRoot>;
}> {
  const [unifiedMod, remarkParseMod, remarkGfmMod] = await Promise.all([
    import('unified'),
    import('remark-parse'),
    import('remark-gfm'),
  ]);
  const createProcessor = () =>
    unifiedMod.unified().use(remarkParseMod.default).use(remarkGfmMod.default);
  return { baseProcessor: createProcessor(), createProcessor };
}

export type { MdastRoot };
